import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useRealtimeSubscription } from '../../hooks/useRealtimeSubscription';
import { useConnectionStatus } from '../../hooks/useConnectionStatus';
import { orderService } from '../../services/orderService';

import Sidebar from '../../components/ui/Sidebar';
import ProjectSwitcher from '../../components/ui/ProjectSwitcher';
import { CheckCircle2, Loader2, Wifi, AlertCircle } from 'lucide-react';

import ProjectCard from './components/ProjectCard';
import ProjectListItem from './components/ProjectListItem';
import DashboardToolbar from './components/DashboardToolbar';
import ActivityFeed from './components/ActivityFeed';
import WorkspaceStats from './components/WorkspaceStats';
import CreateProjectModal from './components/CreateProjectModal';
import DeleteConfirmModal from './components/DeleteConfirmModal';


export default function ProjectDashboard() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, profile, loading, signOut } = useAuth();
  
  // Check if user just completed payment
  const paymentSuccess = location?.state?.paymentSuccess;
  const newWorkspaceName = location?.state?.workspaceName;

  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [viewMode, setViewMode] = useState('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('modified');
  const [filterStatus, setFilterStatus] = useState('all');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [showWelcomeMessage, setShowWelcomeMessage] = useState(false);
  const [welcomeData, setWelcomeData] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // Updated state - real data from Supabase
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [isLoadingProjects, setIsLoadingProjects] = useState(true);
  const [realtimeConnected, setRealtimeConnected] = useState(false);

  const { isConnected, queueStatus } = useConnectionStatus();

  // Load projects from Supabase
  useEffect(() => {
    const loadProjects = async () => {
      if (!user?.id) return;
      
      try {
        setIsLoadingProjects(true);
        const orders = await orderService?.getUserOrders(user?.id);
        
        // Transform orders to project format
        const projectData = orders?.map(order => ({
          id: order?.id,
          name: order?.projectName || 'Untitled Project',
          description: order?.projectDescription || '',
          status: order?.status || 'draft',
          lastModified: order?.updatedAt,
          projectData: order?.projectData,
          orderNumber: order?.orderNumber,
          paymentStatus: order?.paymentStatus
        }));
        
        setProjects(projectData);
      } catch (error) {
        console.error('Failed to load projects:', error);
      } finally {
        setIsLoadingProjects(false);
      }
    };

    loadProjects();
  }, [user?.id]);

  // Real-time subscription for projects (orders)
  const { isConnected: projectsConnected } = useRealtimeSubscription({
    table: 'orders',
    event: '*',
    filter: user?.id ? { column: 'user_id', value: user?.id } : null,
    enabled: !!user?.id,
    onChange: async (payload) => {
      const { eventType, new: newRecord, old: oldRecord } = payload;
      
      if (eventType === 'INSERT' && newRecord) {
        // New project created
        const newProject = {
          id: newRecord?.id,
          name: newRecord?.project_name || 'Untitled Project',
          description: newRecord?.project_description || '',
          status: newRecord?.status || 'draft',
          lastModified: newRecord?.updated_at,
          projectData: newRecord?.project_data,
          orderNumber: newRecord?.order_number,
          paymentStatus: newRecord?.payment_status
        };
        
        setProjects(prev => [newProject, ...prev]);
        
        // Show notification
        setSuccessMessage(`New project "${newProject?.name}" created`);
        setTimeout(() => setSuccessMessage(null), 3000);
        
      } else if (eventType === 'UPDATE' && newRecord) {
        // Project updated
        setProjects(prev => 
          prev?.map(p => 
            p?.id === newRecord?.id 
              ? {
                  ...p,
                  name: newRecord?.project_name || p?.name,
                  description: newRecord?.project_description || p?.description,
                  status: newRecord?.status || p?.status,
                  lastModified: newRecord?.updated_at,
                  projectData: newRecord?.project_data,
                  paymentStatus: newRecord?.payment_status
                }
              : p
          )
        );
        
      } else if (eventType === 'DELETE' && oldRecord) {
        // Project deleted
        setProjects(prev => prev?.filter(p => p?.id !== oldRecord?.id));
        
        setSuccessMessage('Project deleted successfully');
        setTimeout(() => setSuccessMessage(null), 3000);
      }
    }
  });

  // Real-time subscription for workspace changes
  const { isConnected: workspaceConnected } = useRealtimeSubscription({
    table: 'workspaces',
    event: 'UPDATE',
    enabled: !!user?.id,
    onChange: (payload) => {
      console.log('Workspace updated:', payload);
      // Workspace updates can be handled here if needed
    }
  });

  // Real-time subscription for team member changes
  const { isConnected: teamConnected } = useRealtimeSubscription({
    table: 'workspace_members',
    event: '*',
    enabled: !!user?.id,
    onChange: (payload) => {
      console.log('Team member change:', payload);
      // Handle team member additions/removals
    }
  });

  // Update overall connection status
  useEffect(() => {
    setRealtimeConnected(projectsConnected || workspaceConnected || teamConnected);
  }, [projectsConnected, workspaceConnected, teamConnected]);

  // Simplified auth check - only redirect if definitely not authenticated
  useEffect(() => {
    if (!loading && !user) {
      navigate('/login', { 
        state: { 
          from: location?.pathname,
          message: 'Please sign in to access your dashboard' 
        } 
      });
    }
  }, [loading, user, navigate, location?.pathname]);

  // Show welcome message after successful payment
  useEffect(() => {
    if (paymentSuccess) {
      setSuccessMessage(
        newWorkspaceName 
          ? `Welcome to ${newWorkspaceName}! Your subscription is now active.`
          : 'Welcome! Your subscription is now active.'
      );
      
      setTimeout(() => {
        setSuccessMessage(null);
      }, 5000);
    }
  }, [paymentSuccess, newWorkspaceName]);

  useEffect(() => {
    let result = [...projects];

    if (searchQuery?.trim()) {
      result = result?.filter(
        (project) =>
        project?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        project?.description?.toLowerCase()?.includes(searchQuery?.toLowerCase())
      );
    }

    if (filterStatus !== 'all') {
      result = result?.filter((project) => project?.status === filterStatus);
    }

    result?.sort((a, b) => {
      switch (sortBy) {
        case 'modified':
          return new Date(b.lastModified) - new Date(a.lastModified);
        case 'created':
          return new Date(b.lastModified) - new Date(a.lastModified);
        case 'name':
          return a?.name?.localeCompare(b?.name);
        case 'status':
          return a?.status?.localeCompare(b?.status);
        default:
          return 0;
      }
    });

    setFilteredProjects(result);
  }, [searchQuery, sortBy, filterStatus, projects]);

  const handleEditProject = (project) => {
    navigate('/visual-builder');
  };

  const handlePreviewProject = (project) => {
    window.open(`/preview/${project?.id}`, '_blank');
  };

  const handleDuplicateProject = (project) => {
    const duplicatedProject = {
      ...project,
      id: Date.now()?.toString(),
      name: `${project?.name} (Copy)`,
      status: 'draft',
      lastModified: new Date()?.toISOString(),
    };
    setProjects([duplicatedProject, ...projects]);
  };

  const handleDeleteProject = (project) => {
    setProjectToDelete(project);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = () => {
    if (projectToDelete) {
      setProjects(projects?.filter((p) => p?.id !== projectToDelete?.id));
      setIsDeleteModalOpen(false);
      setProjectToDelete(null);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/login', {
        state: { message: 'You have been successfully logged out' }
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  // Updated stats calculation
  const mockStats = {
    totalProjects: projects?.length,
    activeProjects: projects?.filter(p => p?.status === 'active')?.length,
    draftProjects: projects?.filter(p => p?.status === 'draft')?.length,
    completedProjects: projects?.filter(p => p?.status === 'completed')?.length,
  };

  const mockActivities = [];

  // Simplified loading display
  if (loading || isLoadingProjects) {
    return (
      <div className="flex h-screen bg-gray-50 items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
          <p className="text-muted-foreground">Loading workspace...</p>
        </div>
      </div>
    );
  }

  // Don't render if no user (should redirect)
  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Sidebar
        isCollapsed={isSidebarCollapsed}
        onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
        user={profile}
        onLogout={handleLogout}
      />
      <div className="flex-1 flex flex-col overflow-hidden">
        <div className="hidden lg:block">
          <ProjectSwitcher
            currentProject={filteredProjects?.[0] || null}
            onProjectChange={(project) => handleEditProject(project)} />
        </div>

        <main className="flex-1 overflow-y-auto bg-gray-50 p-6">
          {/* Real-time connection indicator */}
          {realtimeConnected && (
            <div className="mb-4 flex items-center justify-end">
              <div className="flex items-center space-x-2 text-xs text-muted-foreground bg-green-50 px-3 py-1.5 rounded-full">
                <Wifi className="w-3.5 h-3.5 text-green-600" />
                <span>Real-time sync active</span>
              </div>
            </div>
          )}

          {/* Success Message from Payment */}
          {successMessage && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl flex items-start animate-fade-in">
              <CheckCircle2 className="w-5 h-5 text-green-600 mr-3 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-green-800 font-medium">Success!</p>
                <p className="text-green-700 text-sm">{successMessage}</p>
              </div>
            </div>
          )}

          {/* Connection Status Info */}
          {!isConnected && (
            <div className="max-w-7xl mx-auto px-8 pt-4">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-yellow-600" />
                  <div>
                    <p className="text-sm font-medium text-yellow-800">
                      Working offline - Changes will sync automatically when connection is restored
                    </p>
                    {queueStatus?.total > 0 && (
                      <p className="text-xs text-yellow-600 mt-1">
                        {queueStatus?.total} change{queueStatus?.total > 1 ? 's' : ''} pending sync
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="max-w-7xl mx-auto">
            <div className="mb-6 md:mb-8">
              <h1 className="font-heading font-bold text-2xl md:text-3xl lg:text-4xl text-foreground mb-2">
                My Projects
              </h1>
              <p className="text-sm md:text-base text-muted-foreground">
                Manage and organize your web applications
              </p>
            </div>

            <DashboardToolbar
              viewMode={viewMode}
              onViewModeChange={setViewMode}
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              sortBy={sortBy}
              onSortChange={setSortBy}
              filterStatus={filterStatus}
              onFilterChange={setFilterStatus}
              onCreateNew={() => setIsCreateModalOpen(true)}
              onOpenTemplates={() => navigate('/template-marketplace')} />

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2">
                {filteredProjects?.length === 0 ? (
                  <div className="bg-card border border-border rounded-lg p-8 md:p-12 text-center">
                    <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg
                        className="w-8 h-8 text-muted-foreground"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                    </div>
                    <h3 className="font-heading font-semibold text-lg text-foreground mb-2">
                      No projects found
                    </h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      {searchQuery || filterStatus !== 'all' ?'Try adjusting your filters or search query' :'Get started by creating your first project'}
                    </p>
                  </div>
                ) : viewMode === 'grid' ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5 lg:gap-6">
                    {filteredProjects?.map((project) => (
                      <ProjectCard
                        key={project?.id}
                        project={project}
                        onEdit={handleEditProject}
                        onPreview={handlePreviewProject}
                        onDuplicate={handleDuplicateProject}
                        onDelete={handleDeleteProject}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="space-y-4">
                    {filteredProjects?.map((project) => (
                      <ProjectListItem
                        key={project?.id}
                        project={project}
                        onEdit={handleEditProject}
                        onPreview={handlePreviewProject}
                        onDuplicate={handleDuplicateProject}
                        onDelete={handleDeleteProject}
                      />
                    ))}
                  </div>
                )}
              </div>

              <div className="space-y-6">
                <WorkspaceStats stats={mockStats} />
                <ActivityFeed activities={mockActivities} />
              </div>
            </div>
          </div>
        </main>
      </div>
      <CreateProjectModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)} />
      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setProjectToDelete(null);
        }}
        onConfirm={confirmDelete}
        projectName={projectToDelete?.name} />
    </div>
  );
}